/**
 * Plot frame shared by every Recharts chart: token-sized height, the
 * responsive container and whether the reader has engaged the plot, which
 * gates every tooltip inside it (`chartTooltipIntent`). For charts that pick a category, Enter on the focused
 * Recharts surface is delegated to the frame.
 */
import React, { useEffect, useRef } from 'react';
import { ResponsiveContainer } from 'recharts';
import type { FmChartSize } from './chartTypes';
import { CHART_PLOT_HEIGHT } from './FmChartChrome.constants';
import { ChartTooltipIntentContext, useChartTooltipIntentState } from './chartTooltipIntent';
import './FmChartFrame.css';

const INITIAL_PLOT_WIDTH = 720;

interface FmChartFrameProps {
  size: FmChartSize;
  /** Exact plot height in CSS pixels when a chart needs one (e.g. row count). */
  height?: number;
  children: React.ReactElement;
  /** Delegated keydown from the focused Recharts surface inside the frame. */
  onKeyDown?: (event: KeyboardEvent) => void;
  /** Measured frame width, for leaves whose axis layout depends on it. */
  onWidth?: (width: number) => void;
  'data-testid'?: string;
  'data-kind'?: string;
}

export const FmChartFrame: React.FC<FmChartFrameProps> = ({
  size,
  height,
  children,
  onKeyDown,
  onWidth,
  'data-testid': testId,
  'data-kind': kind,
}) => {
  const plotHeight = height ?? CHART_PLOT_HEIGHT[size];
  const frameRef = useRef<HTMLDivElement>(null);
  const tooltipIntent = useChartTooltipIntentState(frameRef);
  // The focusable element is the Recharts surface, which takes no key props; its
  // keydown is delegated here. Arrow keys stay with the accessibility layer.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !onKeyDown) return undefined;
    frame.addEventListener('keydown', onKeyDown);
    return () => frame.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !onWidth || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      // Whole pixels: sub-pixel resizes would re-lay the axis for nothing.
      if (entry) onWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [onWidth]);
  return (
    <div
      ref={frameRef}
      className={`fm-chart-frame fm-chart-frame--${size}`}
      style={height === undefined ? undefined : { height: plotHeight }}
      data-testid={testId}
      data-chart-kind={kind}
      data-selectable={onKeyDown ? 'true' : undefined}
      data-tooltip-engaged={tooltipIntent ? 'true' : 'false'}
    >
      <ChartTooltipIntentContext.Provider value={tooltipIntent}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          minWidth={1}
          minHeight={1}
          initialDimension={{ width: INITIAL_PLOT_WIDTH, height: plotHeight }}
        >
          {children}
        </ResponsiveContainer>
      </ChartTooltipIntentContext.Provider>
    </div>
  );
};
